import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { MailrailAutoSendEmailCampaign, TUnitOfTime } from '@feathr/blackbox';
import {
  CardContent,
  CardHeader,
  CardV2 as Card,
  Fieldset,
  NumberInput,
  Radios,
  Select,
  Well,
} from '@feathr/components';
import { flattenError } from '@feathr/hooks';
import type { IValidateGrouped } from '@feathr/rachis';

import type { ITimeUnitOption } from '../MailrailCampaignEdit/timeUnits.utils';
import {
  bestUnit,
  getTimeUnitOptions,
  getTimeUnitOptionValue,
  minimumDisplayValues,
  toDisplayValue,
  toSeconds,
} from '../MailrailCampaignEdit/timeUnits.utils';

import * as styles from './MailrailAutomationSchedule.css';

interface IMailrailAutomationScheduleProps {
  campaign: MailrailAutoSendEmailCampaign;
  disabled: boolean;
  validationErrors: IValidateGrouped;
}

function MailrailAutomationSchedule({
  campaign,
  disabled,
  validationErrors,
}: Readonly<IMailrailAutomationScheduleProps>): JSX.Element {
  const { t } = useTranslation();

  const initialDelay = campaign.get('delay_seconds', 0) === 0 ? 'immediately' : 'delay';
  const [delay, setDelay] = useState<'immediately' | 'delay'>(initialDelay);
  const [delayUnit, setDelayUnit] = useState<TUnitOfTime>(() =>
    bestUnit(campaign.get('delay_seconds')),
  );

  const defaults = campaign.getDefaults();
  const isTimeSubtype = campaign.get('subtype') === 'time';

  const delayDisplayValue = toDisplayValue(campaign.get('delay_seconds'), delayUnit);

  function onDelayChange(newValue?: string): void {
    if (newValue === 'immediately') {
      campaign.set({ delay_seconds: 0 });
      setDelay(newValue);
    }

    // If delay is selected, set send schedule fields back to default values
    if (newValue === 'delay') {
      campaign.set({ delay_seconds: defaults.delay_seconds });
      setDelay(newValue);
    }
  }

  function handleChangeDelayValue(newValue?: number): void {
    if (newValue !== undefined) {
      campaign.set({ delay_seconds: toSeconds(newValue, delayUnit) });
    }
  }

  function getOptionLabelDelay(option: ITimeUnitOption): string | undefined {
    return {
      minutes: t('minute', { count: delayDisplayValue }),
      hours: t('hour', { count: delayDisplayValue }),
      days: t('day', { count: delayDisplayValue }),
      weeks: t('week', { count: delayDisplayValue }),
    }[option.id];
  }

  function handleChangeDelayUnit(option: ITimeUnitOption): void {
    setDelayUnit(option.id);
    campaign.set({ delay_seconds: toSeconds(minimumDisplayValues[option.id], option.id) });
  }

  const scheduleInputs = (
    <Fieldset>
      {!isTimeSubtype && (
        <Radios
          dataName={'send_schedule'}
          disabled={disabled}
          helpText={t('Send your email immediately following the trigger, or after a delay.')}
          label={t('Send schedule')}
          layout={'block'}
          onChange={onDelayChange}
          options={[
            { id: 'immediately', name: t('Immediately') },
            { id: 'delay', name: t('Delay') },
          ]}
          required={true}
          value={delay}
        />
      )}
      {delay === 'delay' && !isTimeSubtype && (
        <NumberInput
          className={styles.delay}
          clearableClassName={styles.delayElement}
          disabled={disabled}
          helpText={t(
            'The amount of time between the person performing the action and sending the email.',
          )}
          label={t('Delay time')}
          min={1}
          name={'delay_seconds'}
          onChange={handleChangeDelayValue}
          suffix={
            <Select
              disabled={disabled}
              getOptionLabel={getOptionLabelDelay}
              getOptionValue={getTimeUnitOptionValue}
              name={'delay_unit'}
              onSelectSingle={handleChangeDelayUnit}
              options={getTimeUnitOptions()}
              value={getTimeUnitOptions().find((o) => o.id === delayUnit)}
            />
          }
          validationError={flattenError(validationErrors.delay_seconds)}
          value={delayDisplayValue}
        />
      )}
    </Fieldset>
  );

  return campaign.isMailrailDrip ? (
    <Well layout={'vertical'} theme={'white'}>
      {scheduleInputs}
    </Well>
  ) : (
    <Card width={'full'}>
      <CardHeader title={t('Schedule')} />
      <CardContent>{scheduleInputs}</CardContent>
    </Card>
  );
}

export default observer(MailrailAutomationSchedule);
